import { useRef, type CSSProperties, type ReactNode } from "react";
import { useFocusTrap } from "./focusTrap.js";

/**
 * Sprint 18 Wave A — generic side-drawer primitive.
 *
 * Builds on the focus-trap discipline established by Sprint 17 Wave F's
 * `MobileNavDrawer` (Tab cycle wraps last → first; Shift+Tab wraps
 * first → last; Esc closes; scrim click closes; initial focus moves
 * into the panel). This generic version is what feature pages use to
 * render trip-detail / event-detail / vehicle-detail / etc. side
 * panels — see Wave B/C/D consumers.
 *
 * GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01 moved that
 * logic into the shared `focusTrap.ts` module so the new centred
 * `Modal` can reuse it instead of growing a second copy. The observable
 * Drawer behaviour is unchanged apart from one strict improvement:
 * **focus now returns to the control that opened the drawer** when it
 * closes (previously focus was dropped on `document.body`).
 *
 * Accessibility contract:
 *   - When `open=true`: rendered as `<dialog>`-shaped DOM with
 *     `role="dialog"` + `aria-modal="true"` + `aria-label` as the sole
 *     accessible-name source (callers pass `ariaLabel`; there is no
 *     `aria-labelledby`/heading-id mechanism since no consumer can
 *     currently supply one).
 *   - Initial focus moves to the first focusable element inside the
 *     panel on mount.
 *   - Esc fires `onClose`.
 *   - Click on the scrim (outside the panel) fires `onClose`.
 *   - Tab from the last focusable wraps to the first; Shift+Tab from
 *     the first wraps to the last. (Same shape as MobileNavDrawer.)
 *   - On close, focus returns to the invoking control.
 *   - When `open=false`: renders nothing.
 *
 * Visual rules:
 *   - Right-side panel by default. Width 420 px on desktop; 100 dvw on
 *     mobile.
 *   - Uses existing `--gt-sys-*` tokens. No custom colors.
 *   - Pages own their own header / scroll / padding inside `children`.
 */
export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name for the dialog. */
  ariaLabel: string;
  /** Drawer side (default `end` = right in LTR, left in RTL). */
  side?: "start" | "end";
  /**
   * Panel inline-size override. Defaults to the
   * `--gt-cmp-drawer-inline-size` component token.
   */
  width?: number | string;
  children: ReactNode;
  /** Extra style on the panel container. */
  style?: CSSProperties;
}

export function Drawer({
  open,
  onClose,
  ariaLabel,
  side = "end",
  width = "var(--gt-cmp-drawer-inline-size)",
  children,
  style,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  const { onKeyDown } = useFocusTrap({ open, containerRef: panelRef, onClose });

  if (!open) return null;

  const panelStyle: CSSProperties = {
    position: "fixed",
    insetBlock: 0,
    [side === "end" ? "insetInlineEnd" : "insetInlineStart"]: 0,
    inlineSize: width,
    maxInlineSize: "100dvw",
    background: "var(--gt-sys-surface)",
    color: "var(--gt-sys-text)",
    boxShadow:
      side === "end" ? "var(--gt-cmp-drawer-shadow-end)" : "var(--gt-cmp-drawer-shadow-start)",
    overflowY: "auto",
    outline: "none",
    zIndex: "var(--gt-cmp-drawer-panel-z)",
    ...style,
  };

  return (
    <>
      {/* Scrim. role="presentation" + onClick = standard pattern. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "var(--gt-cmp-drawer-scrim)",
          zIndex: "var(--gt-cmp-drawer-scrim-z)",
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={panelStyle}
      >
        {children}
      </div>
    </>
  );
}
